import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, EyeSlash } from "@phosphor-icons/react";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let user;
      if (tab === "login") {
        user = await login(email, password);
      } else {
        user = await register(email, password, name);
      }
      navigate(user?.onboarded ? "/home" : "/onboarding");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex justify-center mb-10">
          <span className="font-heading tracking-[0.25em] text-2xl text-[#2D1B0E]">
            VO<span className="text-[#C4A265]">&#10013;</span>CE
          </span>
        </Link>

        <div className="glass-card-strong p-7" data-testid="auth-card">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#EDE6DC]/60 mb-5 rounded-full p-1">
              <TabsTrigger value="login" data-testid="login-tab"
                className="font-body text-sm rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2D1B0E] data-[state=active]:shadow-sm text-[#8B7355]">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab"
                className="font-body text-sm rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2D1B0E] data-[state=active]:shadow-sm text-[#8B7355]">
                Sign Up
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit}>
              <TabsContent value="register" className="mt-0 space-y-3">
                <div>
                  <label className="text-xs text-[#8B7355] font-body block mb-1">Full Name</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                    required={tab === "register"} data-testid="register-name-input"
                    className="bg-white/60 border-[#E8DFD3] text-[#2D1B0E] placeholder:text-[#B8A088] font-body rounded-xl" />
                </div>
              </TabsContent>

              <div className="space-y-3 mt-0">
                <div>
                  <label className="text-xs text-[#8B7355] font-body block mb-1">Email</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" required data-testid="email-input"
                    className="bg-white/60 border-[#E8DFD3] text-[#2D1B0E] placeholder:text-[#B8A088] font-body rounded-xl" />
                </div>
                <div>
                  <label className="text-xs text-[#8B7355] font-body block mb-1">Password</label>
                  <div className="relative">
                    <Input type={showPw ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required
                      data-testid="password-input"
                      className="bg-white/60 border-[#E8DFD3] text-[#2D1B0E] placeholder:text-[#B8A088] font-body rounded-xl pr-10" />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8A088] hover:text-[#8B7355]">
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && <p className="text-red-500 text-xs font-body" data-testid="auth-error">{error}</p>}

                <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
                  className="w-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full py-5 font-body text-sm">
                  {loading ? "Please wait..." : tab === "login" ? "Sign In" : "Create Account"}
                </Button>
              </div>
            </form>
          </Tabs>
        </div>

        <p className="text-center text-[#B8A088] text-[10px] font-body mt-5">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
